import { useMutation } from '@tanstack/react-query'
import invariant from 'tiny-invariant'

import useAppDispatch from '@/hooks/useAppDispatch'
import {
  USER_CHANGED,
  useUserContextChanged,
} from '@/hooks/useUserContextChanged'
import { LOCAL_STORAGE_KEYS } from '@/lib/constants'
import { isOfficeAddIn } from '@/lib/helpers/officeHelpers'
import { MUTATION_KEYS } from '@/lib/mutationKeys'
import authService from '@/lib/services/authService'
import errorTypeService from '@/lib/services/errorTypeService'
import posthogService from '@/lib/services/posthogService'
import sentryService from '@/lib/services/sentryService'
import sessionService from '@/lib/services/sessionService'
import {
  initialiseSessionError,
  initialiseSessionFail,
  initialiseSessionSuccess,
} from '@/redux/slices/session'

export function useCreateSessionMetadataMutation() {
  const dispatch = useAppDispatch()
  const { postContextChange } = useUserContextChanged()

  return useMutation({
    mutationKey: MUTATION_KEYS.sessionMetadata(),
    mutationFn: async () => {
      const { isAuthenticated } = await authService.initialize()

      if (!isAuthenticated) {
        dispatch(initialiseSessionFail())
        return
      }

      // User is logged in, so let's fetch session data from the API
      const sessionMetadata = await sessionService.createSessionMetadata()

      const { redirectUri } = sessionMetadata

      if (
        redirectUri &&
        redirectUri !== window.location.href &&
        redirectUri + '/' !== window.location.href
      ) {
        window.location.assign(redirectUri)
      }

      const { currentUser, currentCompany, currentUserType } = sessionMetadata

      invariant(currentUser, 'Expected user to be set')

      posthogService.identifyUserAndCurrentCompanyGroup(sessionMetadata)

      sentryService.setAppContext({
        currentUser: currentUser,
        currentCompany,
        currentUserType,
        isOutlook: isOfficeAddIn(),
      })

      dispatch(
        initialiseSessionSuccess({
          metadata: sessionMetadata,
        }),
      )

      return sessionMetadata
    },
    onSuccess: (sessionMetadata) => {
      if (sessionMetadata?.currentUser) {
        postContextChange(USER_CHANGED, {
          user: { id: sessionMetadata.currentUser.id },
        })
      }

      if (sessionMetadata?.commitHash) {
        localStorage.setItem(
          LOCAL_STORAGE_KEYS.apiCommitHash,
          sessionMetadata.commitHash,
        )
      }
    },
    onError: async (error) => {
      dispatch(initialiseSessionError())

      // Don't auto-logout when registration was blocked (email is a verified
      // public facing alias, or already belongs to an existing user) - let the
      // error boundary render the friendly explanation instead (the user can
      // sign out from there).
      if (errorTypeService.isRegistrationBlockedError(error)) {
        return
      }

      if (errorTypeService.isUnauthorizedError(error)) {
        console.log(
          'Session metadata endpoint returned 401 response. Logging the user out...',
        )
        await authService.logout()
      }
    },
  })
}
